import { c as _c } from "react/compiler-runtime";
import __i18nConfig from '@next-translate-root/i18n.js';
import __appWithI18n from 'next-translate/appWithI18n';
import { Standalone500ErrorPage } from '@/components/Templates/ErrorPageTemplate/Standalone500ErrorPage';
import { ErrorBoundary } from '@/components/Wrappers/ErrorBoundary';
import { getClientRuntimeConfig } from '@/config/runtime/clientRuntimeConfig';
import clientStorage from '@/services/ClientStorage';
import { cleanUpOfflineItems } from '@/services/OfflineStorage';
import { OFFLINE_LANGUAGE_STORAGE_KEY, OFFLINE_READING_FEATURE_FLAG } from '@/services/OfflineStorage/constants';
import { writeBrowserLicense } from '@/services/OfflineStorage/license';
import { startServiceWorkerRegistration, supportsServiceWorker } from '@/services/OfflineStorage/serviceWorker';
import { isBackgroundTabArtifact } from '@/utils/webVitalsVisibility';
import '@/static/css/01.palette.css';
import '@/static/css/02.constants.css';
import '@/static/css/03.colors.css';
import '@/static/css/04.base.css';
import '@xyflow/react/dist/style.css';
import type { NextComponentType } from 'next';
import type { NextWebVitalsMetric } from 'next/app';
import Head from 'next/head';
import Router from 'next/router';
import { useReportWebVitals } from 'next/web-vitals';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import 'pdfjs-dist/legacy/web/pdf_viewer.css';
import { useEffect } from 'react';
import { StyleSheetManager } from 'styled-components';
require('../../polyfills');
const pendingMetrics: NextWebVitalsMetric[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
// The route this document was loaded as, so a client-side navigation before metric flushing doesn't send a wrong URL
// Next 16.4 adds per-navigation attribution (navigationURL), which will supersede this.
let entryPathname: string | null = null;
// Retries until the router exists, so a metric reported before hydration can't strand this null.
const rememberEntryPathname = () => {
  if (entryPathname === null) entryPathname = Router.router?.pathname ?? null;
};
// Flush immediately when the tab is hidden so metrics aren't lost on navigation or browser close
if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushMetrics();
  });
}
const flushMetrics = () => {
  if (pendingMetrics.length === 0) return;
  if (flushTimer !== null) clearTimeout(flushTimer);
  const serverTimings: {
    [key: string]: number;
  } = {};
  // Get Server Timings and add them to the body
  performance.getEntriesByType('navigation').forEach((performanceNavigationTiming: any) => {
    // No Safari support on the serverTiming property
    if (performanceNavigationTiming?.serverTiming) {
      performanceNavigationTiming.serverTiming.forEach((v: any) => {
        serverTimings[v.name] = v.duration;
      });
    }
  });
  navigator?.sendBeacon?.('/api/monitoring', JSON.stringify({
    pathname: entryPathname ?? '',
    serverTimings,
    metrics: pendingMetrics
  }));
  pendingMetrics.length = 0;
};
/**
 * Executes in the browser to report performance stats such as Time To First Byte,
 * First Contentful Paint, etc
 * @see https://nextjs.org/docs/app/guides/analytics
 */
const reportWebVitals = (metric: NextWebVitalsMetric) => {
  rememberEntryPathname();
  // There's a weird bug with Next.js-hydration calling too much
  if (metric.name === 'Next.js-hydration') {
    return;
  }
  if (isBackgroundTabArtifact(metric.name, metric.value) || getClientRuntimeConfig()?.environment === 'local') {
    return;
  }
  pendingMetrics.push(metric);
  if (flushTimer !== null) clearTimeout(flushTimer);
  flushTimer = setTimeout(flushMetrics, 2000);
};
function App(t0) {
  const $ = _c(23);
  const {
    Component,
    pageProps
  } = t0;
  const getLayout = (Component as any)?.getLayout || _temp;
  useReportWebVitals(reportWebVitals);
  const sessionUser = pageProps?.sessionData?.user;
  let t1;
  if ($[0] !== sessionUser) {
    t1 = !!sessionUser && !sessionUser.isGuestUser && !!sessionUser.enabledFeatures?.includes(OFFLINE_READING_FEATURE_FLAG);
    $[0] = sessionUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasOfflineReading = t1;
  let t2;
  let t3;
  if ($[2] !== hasOfflineReading) {
    t2 = () => {
      if (!supportsServiceWorker() || !hasOfflineReading) {
        return;
      }
      return startServiceWorkerRegistration();
    };
    t3 = [hasOfflineReading];
    $[2] = hasOfflineReading;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const userLang = pageProps?.sessionData?.user?.l10n;
  let t4;
  let t5;
  if ($[5] !== hasOfflineReading || $[6] !== userLang) {
    t4 = () => {
      if (hasOfflineReading && userLang) {
        clientStorage.set(OFFLINE_LANGUAGE_STORAGE_KEY, userLang);
      }
    };
    t5 = [hasOfflineReading, userLang];
    $[5] = hasOfflineReading;
    $[6] = userLang;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== hasOfflineReading || $[10] !== sessionUser?.bbUuid) {
    t6 = () => {
      const userId = sessionUser?.bbUuid;
      if (typeof window === "undefined") {
        return;
      }
      if (!hasOfflineReading || userId === undefined) {
        return;
      }
      cleanUpOfflineItems(userId);
      writeBrowserLicense(userId);
    };
    $[9] = hasOfflineReading;
    $[10] = sessionUser?.bbUuid;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = sessionUser?.bbUuid;
  let t8;
  if ($[12] !== hasOfflineReading || $[13] !== t7) {
    t8 = [hasOfflineReading, t7];
    $[12] = hasOfflineReading;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useEffect(t6, t8);
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Standalone500ErrorPage />;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Head><title>BloombergNEF</title><meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" /><meta name="text-scale" content="scale" /></Head>;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== Component || $[18] !== getLayout || $[19] !== pageProps) {
    t11 = getLayout(Component, pageProps);
    $[17] = Component;
    $[18] = getLayout;
    $[19] = pageProps;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11) {
    t12 = <StyleSheetManager enableVendorPrefixes={true}><ErrorBoundary fallback={t9}>{t10}<NuqsAdapter>{t11}</NuqsAdapter></ErrorBoundary></StyleSheetManager>;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
function _temp(X, pageProps_0) {
  return <X {...pageProps_0} />;
}
export default __appWithI18n(App, {
  ...__i18nConfig,
  isLoader: true,
  skipInitialProps: true,
  loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({}))
});